/**
 * Contract content editors for the CA workspace: Schedule of Values, payment milestones,
 * opening position (contracts already running when registered) and the estimate link
 * for contracts registered without an award.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  epcContractAdminService,
  type EpcContentImportPreview,
  type EpcContentKind,
  type EpcContractContent,
  type EpcContractMilestone,
  type EpcSovLine,
} from '@/services/epcContractAdmin.service';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

export const SOV_LINE_TYPES = ['ITEM', 'PROVISIONAL_SUM', 'PRIME_COST', 'DAYWORK'];

type ApiError = { response?: { data?: { message?: string } }; message?: string };
const errMsg = (e: unknown, fallback: string) =>
  (e as ApiError)?.response?.data?.message || (e as ApiError)?.message || fallback;

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Line amount shown while editing: quantity × rate when both set, else the entered amount. */
export function sovLineAmount(l: Pick<EpcSovLine, 'quantity' | 'rate' | 'amount'>): number {
  const q = num(l.quantity);
  const r = num(l.rate);
  if (q != null && r != null) return Math.round(q * r * 100) / 100;
  return num(l.amount) ?? 0;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function useContractContent(contractId: string) {
  return useQuery({
    queryKey: ['epc-contract-content', contractId],
    queryFn: () => epcContractAdminService.getContractContent(contractId),
    enabled: Boolean(contractId),
    staleTime: 10_000,
  });
}

function useContentSaved(contractId: string) {
  const qc = useQueryClient();
  return (data?: EpcContractContent) => {
    if (data) qc.setQueryData(['epc-contract-content', contractId], data);
    qc.invalidateQueries({ queryKey: ['epc-contract-content', contractId] });
    qc.invalidateQueries({ queryKey: ['epc-contract-admin-workspace', contractId] });
    qc.invalidateQueries({ queryKey: ['epc-contracts'] });
  };
}

function ContentSummary({ content, currency }: { content: EpcContractContent; currency: string }) {
  const s = content.summary;
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space wrap size="large">
        <Text>
          Contract value: <Text strong>{formatMoney(s.contractValue, { currency })}</Text>
        </Text>
        <Text>
          Schedule of Values: <Text strong>{formatMoney(s.sovTotal, { currency })}</Text> ({s.sovCount}{' '}
          lines)
        </Text>
        <Text>
          Milestones: <Text strong>{s.milestoneCount}</Text>
          {s.milestoneCount ? ` · ${s.milestonePercentTotal}% · ${formatMoney(s.milestoneAmountTotal, { currency })}` : ''}
        </Text>
        {!content.editable ? <Tag>Locked — changes go through Variations</Tag> : null}
      </Space>
      {s.issues.length ? (
        <Alert
          type="warning"
          showIcon
          message="Fix before submitting for approval"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {s.issues.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          }
        />
      ) : null}
      {s.advisories?.length ? (
        <Alert
          type="info"
          showIcon
          message="Suggested for this commercial model"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {s.advisories.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          }
        />
      ) : null}
    </Space>
  );
}

function ExcelActions({
  contractId,
  kind,
  editable,
  onCommitted,
}: {
  contractId: string;
  kind: EpcContentKind;
  editable: boolean;
  onCommitted: (data: EpcContractContent) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<EpcContentImportPreview | null>(null);
  const [setValue, setSetValue] = useState(false);
  const label = kind === 'sov' ? 'schedule-of-values' : 'payment-milestones';

  const previewM = useMutation({
    mutationFn: (f: File) => epcContractAdminService.previewContentImport(contractId, kind, f),
    onSuccess: (p) => setPreview(p),
    onError: (e) => message.error(errMsg(e, 'Could not read file')),
  });
  const commitM = useMutation({
    mutationFn: () =>
      epcContractAdminService.commitContentImport(contractId, kind, file!, {
        setContractValue: kind === 'sov' && setValue,
      }),
    onSuccess: (data) => {
      message.success('Imported');
      setPreview(null);
      setFile(null);
      onCommitted(data);
    },
    onError: (e) => message.error(errMsg(e, 'Import failed')),
  });

  return (
    <>
      <Space wrap>
        <Button
          icon={<DownloadOutlined />}
          onClick={async () => {
            try {
              saveBlob(await epcContractAdminService.downloadContentTemplate(kind), `${label}-template.xlsx`);
            } catch (e) {
              message.error(errMsg(e, 'Download failed'));
            }
          }}
        >
          Template
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={async () => {
            try {
              saveBlob(await epcContractAdminService.exportContent(contractId, kind), `${label}.xlsx`);
            } catch (e) {
              message.error(errMsg(e, 'Export failed'));
            }
          }}
        >
          Export
        </Button>
        <Upload
          accept=".xlsx,.xls"
          showUploadList={false}
          disabled={!editable}
          beforeUpload={(f) => {
            setFile(f);
            previewM.mutate(f);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} disabled={!editable} loading={previewM.isPending}>
            Import Excel
          </Button>
        </Upload>
      </Space>
      <Modal
        open={Boolean(preview)}
        title={`Import preview — ${preview?.validCount ?? 0} valid, ${preview?.errorCount ?? 0} with errors`}
        width={900}
        onCancel={() => setPreview(null)}
        okText="Replace with this file"
        okButtonProps={{ disabled: !preview?.ready, loading: commitM.isPending }}
        onOk={() => commitM.mutate()}
      >
        <Alert
          type={preview?.ready ? 'info' : 'error'}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            preview?.ready
              ? 'Importing replaces the current list. Lines are matched by code, so existing IPC references are kept.'
              : 'Fix the rows with errors in Excel and upload again.'
          }
        />
        {kind === 'sov' ? (
          <Checkbox checked={setValue} onChange={(e) => setSetValue(e.target.checked)} style={{ marginBottom: 12 }}>
            Set the contract value to the Schedule of Values total
          </Checkbox>
        ) : null}
        <Table
          size="small"
          rowKey="row"
          pagination={{ pageSize: 10 }}
          dataSource={preview?.rows || []}
          columns={[
            { title: 'Row', dataIndex: 'row', width: 60 },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 80,
              render: (s: string) => <Tag color={s === 'valid' ? 'green' : 'red'}>{s}</Tag>,
            },
            {
              title: kind === 'sov' ? 'Item / description' : 'Milestone / title',
              key: 'd',
              render: (_: unknown, r) =>
                kind === 'sov'
                  ? `${String(r.data.itemCode || '')} ${String(r.data.description || '')}`
                  : `${String(r.data.milestoneNumber || '')} ${String(r.data.title || '')}`,
            },
            { title: 'Errors', dataIndex: 'errors', render: (e: string[]) => e.join('; ') || '—' },
          ]}
        />
      </Modal>
    </>
  );
}

type SovRow = EpcSovLine & { _key: string; milestone_number?: string | null };

export const EpcSovEditor: React.FC<{ contractId: string; currency: string }> = ({
  contractId,
  currency,
}) => {
  const contentQ = useContractContent(contractId);
  const onSaved = useContentSaved(contractId);
  const [rows, setRows] = useState<SovRow[]>([]);
  const [setValue, setSetValue] = useState(false);
  const content = contentQ.data;
  const editable = Boolean(content?.editable);

  const msNumberById = useMemo(
    () => new Map((content?.milestones || []).map((m) => [String(m.id), m.milestone_number])),
    [content?.milestones]
  );

  useEffect(() => {
    if (!content) return;
    setRows(
      content.sovLines.map((l, i) => ({
        ...l,
        _key: String(l.id || i),
        milestone_number: l.milestone_id ? msNumberById.get(String(l.milestone_id)) || null : null,
      }))
    );
  }, [content, msNumberById]);

  const total = rows.reduce((s, r) => s + sovLineAmount(r), 0);

  const saveM = useMutation({
    mutationFn: () =>
      epcContractAdminService.replaceSovLines(
        contractId,
        rows.map((r) => ({
          itemCode: r.item_code,
          description: r.description,
          unit: r.unit,
          quantity: r.quantity,
          rate: r.rate,
          amount: num(r.quantity) != null && num(r.rate) != null ? null : r.amount,
          lineType: r.line_type,
          wbsCode: r.wbs_code,
          milestoneNumber: r.milestone_number || null,
        })),
        { setContractValue: setValue }
      ),
    onSuccess: (data) => {
      message.success('Schedule of Values saved');
      onSaved(data);
    },
    onError: (e) => message.error(errMsg(e, 'Save failed')),
  });

  const update = (key: string, patch: Partial<SovRow>) =>
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  if (contentQ.isError) return <Alert type="error" message={errMsg(contentQ.error, 'Failed to load')} />;
  if (!content) return null;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <ContentSummary content={content} currency={currency} />
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <ExcelActions contractId={contractId} kind="sov" editable={editable} onCommitted={onSaved} />
        <Text>
          Editing total: <Text strong>{formatMoney(total, { currency })}</Text>
        </Text>
      </Space>
      <Table
        size="small"
        rowKey="_key"
        dataSource={rows}
        pagination={false}
        scroll={{ x: 1200 }}
        columns={[
          {
            title: 'Item code',
            dataIndex: 'item_code',
            width: 110,
            render: (v, r) => (
              <Input value={v} disabled={!editable} onChange={(e) => update(r._key, { item_code: e.target.value })} />
            ),
          },
          {
            title: 'Description',
            dataIndex: 'description',
            render: (v, r) => (
              <Input value={v} disabled={!editable} onChange={(e) => update(r._key, { description: e.target.value })} />
            ),
          },
          {
            title: 'Unit',
            dataIndex: 'unit',
            width: 80,
            render: (v, r) => (
              <Input value={v || ''} disabled={!editable} onChange={(e) => update(r._key, { unit: e.target.value })} />
            ),
          },
          {
            title: 'Qty',
            dataIndex: 'quantity',
            width: 110,
            render: (v, r) => (
              <InputNumber
                value={num(v)}
                min={0}
                disabled={!editable}
                style={{ width: '100%' }}
                onChange={(n) => update(r._key, { quantity: n })}
              />
            ),
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            width: 130,
            render: (v, r) => (
              <InputNumber
                value={num(v)}
                min={0}
                disabled={!editable}
                style={{ width: '100%' }}
                onChange={(n) => update(r._key, { rate: n })}
              />
            ),
          },
          {
            title: 'Amount',
            key: 'amount',
            width: 150,
            render: (_: unknown, r) =>
              num(r.quantity) != null && num(r.rate) != null ? (
                formatMoney(sovLineAmount(r), { currency })
              ) : (
                <InputNumber
                  value={num(r.amount)}
                  min={0}
                  disabled={!editable}
                  style={{ width: '100%' }}
                  placeholder="Lump sum"
                  onChange={(n) => update(r._key, { amount: n })}
                />
              ),
          },
          {
            title: 'Type',
            dataIndex: 'line_type',
            width: 150,
            render: (v, r) => (
              <Select
                value={v || 'ITEM'}
                disabled={!editable}
                style={{ width: '100%' }}
                options={SOV_LINE_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') }))}
                onChange={(t) => update(r._key, { line_type: t })}
              />
            ),
          },
          {
            title: 'WBS',
            dataIndex: 'wbs_code',
            width: 100,
            render: (v, r) => (
              <Input value={v || ''} disabled={!editable} onChange={(e) => update(r._key, { wbs_code: e.target.value })} />
            ),
          },
          {
            title: 'Milestone',
            dataIndex: 'milestone_number',
            width: 120,
            render: (v, r) => (
              <Select
                allowClear
                value={v || undefined}
                disabled={!editable}
                style={{ width: '100%' }}
                options={(content.milestones || []).map((m) => ({
                  value: m.milestone_number,
                  label: m.milestone_number,
                }))}
                onChange={(m) => update(r._key, { milestone_number: m || null })}
              />
            ),
          },
          {
            title: '',
            key: 'del',
            width: 50,
            render: (_: unknown, r) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={!editable}
                onClick={() => setRows((prev) => prev.filter((x) => x._key !== r._key))}
              />
            ),
          },
        ]}
      />
      {editable ? (
        <Space wrap>
          <Button
            icon={<PlusOutlined />}
            onClick={() =>
              setRows((prev) => [
                ...prev,
                {
                  _key: `new-${Date.now()}`,
                  item_code: String(prev.length + 1),
                  description: '',
                  line_type: 'ITEM',
                },
              ])
            }
          >
            Add line
          </Button>
          <Checkbox checked={setValue} onChange={(e) => setSetValue(e.target.checked)}>
            Set contract value to this total
          </Checkbox>
          <Button type="primary" loading={saveM.isPending} onClick={() => saveM.mutate()}>
            Save Schedule of Values
          </Button>
        </Space>
      ) : null}
    </Space>
  );
};

type MsRow = EpcContractMilestone & { _key: string };

export const EpcMilestonesEditor: React.FC<{ contractId: string; currency: string }> = ({
  contractId,
  currency,
}) => {
  const contentQ = useContractContent(contractId);
  const onSaved = useContentSaved(contractId);
  const [rows, setRows] = useState<MsRow[]>([]);
  const content = contentQ.data;
  const editable = Boolean(content?.editable);

  useEffect(() => {
    if (!content) return;
    setRows(content.milestones.map((m, i) => ({ ...m, _key: String(m.id || i) })));
  }, [content]);

  const contractValue = content?.summary.contractValue || 0;
  const pctTotal = rows.reduce((s, r) => s + (num(r.percent) ?? 0), 0);

  const saveM = useMutation({
    mutationFn: () =>
      epcContractAdminService.replaceMilestones(
        contractId,
        rows.map((r) => ({
          milestoneNumber: r.milestone_number,
          title: r.title,
          milestoneType: r.milestone_type,
          plannedDate: r.planned_date ? String(r.planned_date).slice(0, 10) : null,
          dueDate: r.due_date ? String(r.due_date).slice(0, 10) : null,
          percent: r.percent,
          amount: r.amount,
        }))
      ),
    onSuccess: (data) => {
      message.success('Payment milestones saved');
      onSaved(data);
    },
    onError: (e) => message.error(errMsg(e, 'Save failed')),
  });

  const achieveM = useMutation({
    mutationFn: (v: { milestoneId: string; achieved: boolean }) =>
      epcContractAdminService.setMilestoneAchievement(contractId, v.milestoneId, {
        achieved: v.achieved,
        actualDate: v.achieved ? dayjs().format('YYYY-MM-DD') : null,
      }),
    onSuccess: (data, v) => {
      message.success(v.achieved ? 'Milestone marked achieved' : 'Milestone set back to planned');
      onSaved(data);
    },
    onError: (e) => message.error(errMsg(e, 'Update failed')),
  });

  const update = (key: string, patch: Partial<MsRow>) =>
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));

  if (contentQ.isError) return <Alert type="error" message={errMsg(contentQ.error, 'Failed to load')} />;
  if (!content) return null;
  const achievable = Boolean(content.milestonesAchievable);
  const isAchieved = (s?: string) => ['ACHIEVED', 'COMPLETED', 'CERTIFIED', 'PAID'].includes(String(s || '').toUpperCase());

  const dateCell = (field: 'planned_date' | 'due_date') => (v: unknown, r: MsRow) => (
    <DatePicker
      value={v ? dayjs(String(v)) : null}
      disabled={!editable}
      style={{ width: '100%' }}
      onChange={(d) => update(r._key, { [field]: d ? d.format('YYYY-MM-DD') : null })}
    />
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <ContentSummary content={content} currency={currency} />
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <ExcelActions contractId={contractId} kind="milestones" editable={editable} onCommitted={onSaved} />
        <Text>
          Percent total: <Text strong type={Math.abs(pctTotal - 100) > 0.01 && pctTotal > 0 ? 'danger' : undefined}>{pctTotal}%</Text>
        </Text>
      </Space>
      <Table
        size="small"
        rowKey="_key"
        dataSource={rows}
        pagination={false}
        scroll={{ x: 1100 }}
        columns={[
          {
            title: 'No.',
            dataIndex: 'milestone_number',
            width: 110,
            render: (v, r) => (
              <Input value={v} disabled={!editable} onChange={(e) => update(r._key, { milestone_number: e.target.value })} />
            ),
          },
          {
            title: 'Title',
            dataIndex: 'title',
            render: (v, r) => (
              <Input value={v} disabled={!editable} onChange={(e) => update(r._key, { title: e.target.value })} />
            ),
          },
          { title: 'Planned', dataIndex: 'planned_date', width: 150, render: dateCell('planned_date') },
          { title: 'Due', dataIndex: 'due_date', width: 150, render: dateCell('due_date') },
          {
            title: '%',
            dataIndex: 'percent',
            width: 100,
            render: (v, r) => (
              <InputNumber
                value={num(v)}
                min={0}
                max={100}
                disabled={!editable}
                style={{ width: '100%' }}
                onChange={(n) => update(r._key, { percent: n })}
              />
            ),
          },
          {
            title: 'Amount',
            dataIndex: 'amount',
            width: 150,
            render: (v, r) => (
              <InputNumber
                value={num(v)}
                min={0}
                disabled={!editable}
                style={{ width: '100%' }}
                placeholder={
                  num(r.percent) != null
                    ? String(Math.round(((num(r.percent) as number) / 100) * contractValue * 100) / 100)
                    : undefined
                }
                onChange={(n) => update(r._key, { amount: n })}
              />
            ),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 130,
            render: (v, r) =>
              v ? (
                <Space direction="vertical" size={0}>
                  <Tag color={isAchieved(v) ? 'green' : undefined}>{String(v)}</Tag>
                  {r.actual_date ? <Text type="secondary">{String(r.actual_date).slice(0, 10)}</Text> : null}
                </Space>
              ) : (
                '—'
              ),
          },
          ...(achievable
            ? [
                {
                  title: 'Achievement',
                  key: 'achieve',
                  width: 140,
                  render: (_: unknown, r: MsRow) =>
                    r.id ? (
                      <Button
                        size="small"
                        type={isAchieved(r.status) ? 'default' : 'primary'}
                        loading={achieveM.isPending && achieveM.variables?.milestoneId === r.id}
                        onClick={() => achieveM.mutate({ milestoneId: r.id as string, achieved: !isAchieved(r.status) })}
                      >
                        {isAchieved(r.status) ? 'Undo' : 'Mark achieved'}
                      </Button>
                    ) : null,
                },
              ]
            : []),
          {
            title: '',
            key: 'del',
            width: 50,
            render: (_: unknown, r) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={!editable}
                onClick={() => setRows((prev) => prev.filter((x) => x._key !== r._key))}
              />
            ),
          },
        ]}
      />
      {editable ? (
        <Space>
          <Button
            icon={<PlusOutlined />}
            onClick={() =>
              setRows((prev) => [
                ...prev,
                {
                  _key: `new-${Date.now()}`,
                  milestone_number: `MS-${String(prev.length + 1).padStart(3, '0')}`,
                  title: '',
                },
              ])
            }
          >
            Add milestone
          </Button>
          <Button type="primary" loading={saveM.isPending} onClick={() => saveM.mutate()}>
            Save milestones
          </Button>
        </Space>
      ) : null}
    </Space>
  );
};

export const EpcOpeningPositionForm: React.FC<{ contractId: string; currency: string }> = ({
  contractId,
  currency,
}) => {
  const contentQ = useContractContent(contractId);
  const onSaved = useContentSaved(contractId);
  const [form] = Form.useForm();
  const content = contentQ.data;
  const editable = Boolean(content?.editable);
  const [lineAmounts, setLineAmounts] = useState<Record<string, { qty: number | null; amount: number | null }>>({});

  useEffect(() => {
    if (!content) return;
    const op = content.openingPosition;
    form.setFieldsValue({
      as_of_date: op?.as_of_date ? dayjs(op.as_of_date) : null,
      last_ipc_number: op?.last_ipc_number || '',
      certified_to_date: op?.certified_to_date ?? null,
      retention_held: op?.retention_held ?? null,
      advance_received: op?.advance_received ?? null,
      advance_recovered: op?.advance_recovered ?? null,
      paid_to_date: op?.paid_to_date ?? null,
      notes: op?.notes || '',
    });
    const map: Record<string, { qty: number | null; amount: number | null }> = {};
    for (const l of op?.sov_lines || []) {
      map[l.item_code] = { qty: l.certified_quantity, amount: l.certified_amount };
    }
    setLineAmounts(map);
  }, [content, form]);

  const lineTotal = Object.values(lineAmounts).reduce((s, l) => s + (l.amount ?? 0), 0);

  const saveM = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcContractAdminService.setOpeningPosition(contractId, {
        as_of_date: values.as_of_date ? dayjs(values.as_of_date as string).format('YYYY-MM-DD') : null,
        last_ipc_number: values.last_ipc_number || null,
        certified_to_date: values.certified_to_date ?? 0,
        retention_held: values.retention_held ?? 0,
        advance_received: values.advance_received ?? 0,
        advance_recovered: values.advance_recovered ?? 0,
        paid_to_date: values.paid_to_date ?? 0,
        notes: values.notes || null,
        sov_lines: Object.entries(lineAmounts)
          .filter(([, l]) => (l.amount ?? 0) > 0 || (l.qty ?? 0) > 0)
          .map(([item_code, l]) => ({
            item_code,
            certified_quantity: l.qty,
            certified_amount: l.amount ?? 0,
          })),
      }),
    onSuccess: (data) => {
      message.success('Opening position saved');
      onSaved(data);
    },
    onError: (e) => message.error(errMsg(e, 'Save failed')),
  });

  const clearM = useMutation({
    mutationFn: () => epcContractAdminService.setOpeningPosition(contractId, null),
    onSuccess: (data) => {
      message.success('Opening position cleared');
      onSaved(data);
    },
    onError: (e) => message.error(errMsg(e, 'Clear failed')),
  });

  if (contentQ.isError) return <Alert type="error" message={errMsg(contentQ.error, 'Failed to load')} />;
  if (!content) return null;

  const money = (field: string, label: string, extra?: string) => (
    <Col xs={24} md={8}>
      <Form.Item name={field} label={label} extra={extra}>
        <InputNumber style={{ width: '100%' }} min={0} step={0.01} addonAfter={currency} />
      </Form.Item>
    </Col>
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert
        type="info"
        showIcon
        message="Only for contracts already in progress when registered"
        description="Cumulative values certified before this system. The first IPC uses them as previous certified, retention held and advance recovered. Leave empty for new contracts."
      />
      <Form form={form} layout="vertical" disabled={!editable} onFinish={(v) => saveM.mutate(v)}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item name="as_of_date" label="As of date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="last_ipc_number" label="Last client IPC number">
              <Input placeholder="e.g. IPC-07" />
            </Form.Item>
          </Col>
          {money('certified_to_date', 'Gross certified to date')}
          {money('retention_held', 'Retention held')}
          {money('advance_received', 'Advance received')}
          {money('advance_recovered', 'Advance recovered')}
          {money('paid_to_date', 'Paid to date', 'For reference; receivables stay in Finance')}
          <Col xs={24}>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
        {content.sovLines.length ? (
          <Card size="small" title="Certified to date per Schedule of Values line" style={{ marginBottom: 12 }}>
            <Table
              size="small"
              rowKey={(r) => String(r.item_code)}
              dataSource={content.sovLines}
              pagination={false}
              columns={[
                { title: 'Item', dataIndex: 'item_code', width: 100 },
                { title: 'Description', dataIndex: 'description' },
                {
                  title: 'Line amount',
                  dataIndex: 'amount',
                  width: 140,
                  render: (v) => formatMoney(Number(v || 0), { currency }),
                },
                {
                  title: 'Certified qty',
                  key: 'qty',
                  width: 130,
                  render: (_: unknown, r) => (
                    <InputNumber
                      min={0}
                      disabled={!editable}
                      value={lineAmounts[r.item_code]?.qty ?? null}
                      onChange={(n) =>
                        setLineAmounts((p) => ({
                          ...p,
                          [r.item_code]: { qty: n, amount: p[r.item_code]?.amount ?? null },
                        }))
                      }
                    />
                  ),
                },
                {
                  title: 'Certified amount',
                  key: 'amt',
                  width: 160,
                  render: (_: unknown, r) => (
                    <InputNumber
                      min={0}
                      disabled={!editable}
                      style={{ width: '100%' }}
                      value={lineAmounts[r.item_code]?.amount ?? null}
                      onChange={(n) =>
                        setLineAmounts((p) => ({
                          ...p,
                          [r.item_code]: { qty: p[r.item_code]?.qty ?? null, amount: n },
                        }))
                      }
                    />
                  ),
                },
              ]}
            />
            <Text type="secondary">
              Lines total {formatMoney(lineTotal, { currency })}. Any difference to gross certified to
              date is treated as not allocated to lines.
            </Text>
          </Card>
        ) : null}
        {editable ? (
          <Space>
            <Button type="primary" htmlType="submit" loading={saveM.isPending}>
              Save opening position
            </Button>
            {content.openingPosition ? (
              <Button danger loading={clearM.isPending} onClick={() => clearM.mutate()}>
                Clear
              </Button>
            ) : null}
          </Space>
        ) : (
          <Text type="secondary">Locked while the contract is not Draft / Rejected.</Text>
        )}
      </Form>
    </Space>
  );
};

/** Link an Approved estimate to a contract registered without an award (cost basis for the Original Budget). */
export const EpcContractEstimateBasisCard: React.FC<{ contractId: string; currency: string }> = ({
  contractId,
  currency,
}) => {
  const qc = useQueryClient();
  const [estimateId, setEstimateId] = useState<string | undefined>();
  const [revisionId, setRevisionId] = useState('');
  const estimatesQ = useQuery({
    queryKey: ['epc-estimations'],
    queryFn: () => epcEstimationService.list(),
    staleTime: 60_000,
  });
  const revisionsQ = useQuery({
    queryKey: ['epc-estimation-workspace', estimateId],
    queryFn: () => epcEstimationService.getWorkspace(estimateId!),
    enabled: Boolean(estimateId),
  });
  const approvedRevisions = (
    ((revisionsQ.data as { revisions?: Record<string, unknown>[] } | undefined)?.revisions || []) as Record<
      string,
      unknown
    >[]
  ).filter((r) => String(r.status) === 'Approved');
  const basisQ = useQuery({
    queryKey: ['epc-contract-estimate-basis', contractId],
    queryFn: () => epcCommercialService.getContractEstimateBasis(contractId),
    enabled: Boolean(contractId),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['epc-contract-estimate-basis', contractId] });
  const linkM = useMutation({
    mutationFn: () =>
      epcCommercialService.linkContractEstimate(contractId, {
        estimateRevisionId: revisionId.trim(),
        replace: Boolean(basisQ.data?.estimate_revision_id),
      }),
    onSuccess: () => {
      message.success('Estimate linked');
      setRevisionId('');
      refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Link failed')),
  });
  const unlinkM = useMutation({
    mutationFn: () => epcCommercialService.unlinkContractEstimate(contractId),
    onSuccess: () => {
      message.success('Estimate unlinked');
      refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Unlink failed')),
  });

  const b = basisQ.data as Record<string, unknown> | undefined;
  if (!b) return null;
  const locked = b.status === 'LOCKED';
  return (
    <Card size="small" title="Estimate basis (optional)">
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="Status">
          <Tag color={locked ? 'purple' : b.status === 'PINNED' ? 'green' : undefined}>{String(b.status)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Estimate">
          {b.estimate_number ? `${String(b.estimate_number)} · Rev ${String(b.revision_number ?? '')}` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Estimated cost">
          {b.total_estimated_cost != null ? formatMoney(Number(b.total_estimated_cost), { currency }) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Margin vs contract value">
          {b.margin_pct != null ? `${String(b.margin_pct)}%` : '—'}
        </Descriptions.Item>
      </Descriptions>
      <Text type="secondary" style={{ display: 'block', margin: '8px 0' }}>
        Link an Approved estimate revision to generate the project&apos;s Original Budget from it. Without
        one, enter the Original Budget manually or from Excel on the project.
      </Text>
      {!locked ? (
        <Space wrap>
          <Select
            showSearch
            allowClear
            style={{ width: 300 }}
            placeholder="Estimate"
            optionFilterProp="label"
            loading={estimatesQ.isLoading}
            value={estimateId}
            options={((estimatesQ.data || []) as Record<string, unknown>[]).map((e) => ({
              value: String(e.id),
              label: `${String(e.estimate_number || '')} ${String(e.title || '')}`.trim(),
            }))}
            onChange={(v) => {
              setEstimateId(v);
              setRevisionId('');
            }}
          />
          <Select
            style={{ width: 200 }}
            placeholder="Approved revision"
            disabled={!estimateId}
            loading={revisionsQ.isLoading}
            value={revisionId || undefined}
            notFoundContent="No Approved revision"
            options={approvedRevisions.map((r) => ({
              value: String(r.id),
              label: `Rev ${String(r.revision_label || r.revision_number || '')}`,
            }))}
            onChange={(v) => setRevisionId(v)}
          />
          <Button type="primary" disabled={!revisionId.trim()} loading={linkM.isPending} onClick={() => linkM.mutate()}>
            {b.estimate_revision_id ? 'Replace estimate' : 'Link estimate'}
          </Button>
          {b.estimate_revision_id ? (
            <Button danger loading={unlinkM.isPending} onClick={() => unlinkM.mutate()}>
              Unlink
            </Button>
          ) : null}
        </Space>
      ) : (
        <Text type="secondary">Locked — the Original Budget was generated from this estimate.</Text>
      )}
    </Card>
  );
};
